import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CANVAS_PRESETS, GRID_DENSITIES, FIT_MODES, presetSize } from "@/lib/canvasUtils";
import { Frame, X, Palette, Plus, Trash2, Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MediaInput } from "@/components/bi/MediaInput";
import { FONT_FAMILIES } from "@/components/bi/FontStyleControls";
import { LANGUAGES } from "@/lib/i18n";

const LOCALES = [
  { value: "auto", label: "Auto (browser)" },
  { value: "en-US", label: "English (US) — 1,234.56" },
  { value: "en-GB", label: "English (UK)" },
  { value: "en-IN", label: "English (India) — 1,23,456" },
  { value: "de-DE", label: "Deutsch — 1.234,56" },
  { value: "fr-FR", label: "Français — 1 234,56" },
  { value: "es-ES", label: "Español" },
  { value: "pt-BR", label: "Português (BR)" },
  { value: "ja-JP", label: "日本語" },
];

export function CanvasSettingsDialog({ open, onOpenChange, canvas, onChange, onGridColsChange, onApplyTheme, currentPalette }) {
  const fixed = canvas.preset !== "fluid";

  const setPreset = (v) => {
    const size = presetSize(v);
    onChange({ preset: v, ...(size.width ? { width: size.width, height: size.height } : {}) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="canvas-settings-dialog" className="max-w-md max-h-[86vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Frame className="w-5 h-5 text-[color:var(--brand-ocean)]" />Canvas settings
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Canvas size</Label>
              <Select value={canvas.preset} onValueChange={setPreset}>
                <SelectTrigger data-testid="canvas-preset-select" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CANVAS_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Viewer fit</Label>
              <Select value={canvas.fit} onValueChange={(v) => onChange({ fit: v })} disabled={!fixed}>
                <SelectTrigger data-testid="canvas-fit-select" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIT_MODES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {fixed && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[color:var(--ink-3)]">Width (px)</Label>
                <Input data-testid="canvas-width-input" type="number" min={320} max={20000} value={canvas.width}
                  disabled={canvas.preset !== "custom"}
                  onChange={(e) => onChange({ width: Math.max(320, Number(e.target.value) || 320) })}
                  className="mt-1 bg-[color:var(--surface)]" />
              </div>
              <div>
                <Label className="text-xs text-[color:var(--ink-3)]">Height (px)</Label>
                <Input data-testid="canvas-height-input" type="number" min={320} max={40000} value={canvas.height}
                  disabled={canvas.preset !== "custom"}
                  onChange={(e) => onChange({ height: Math.max(320, Number(e.target.value) || 320) })}
                  className="mt-1 bg-[color:var(--surface)]" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Layout mode</Label>
              <Select value={canvas.layout_mode} onValueChange={(v) => onChange({ layout_mode: v })}>
                <SelectTrigger data-testid="canvas-layout-mode-select" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free-form (place anywhere)</SelectItem>
                  <SelectItem value="compact">Auto-compact (close gaps)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Grid snapping</Label>
              <Select value={String(canvas.grid_cols)} onValueChange={(v) => onGridColsChange(Number(v))}>
                <SelectTrigger data-testid="canvas-grid-select" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GRID_DENSITIES.map((g) => <SelectItem key={g.value} value={String(g.value)}>{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Number & date locale (regional formatting)</Label>
            <Select value={canvas.locale || "auto"} onValueChange={(v) => onChange({ locale: v === "auto" ? "" : v })}>
              <SelectTrigger data-testid="canvas-locale-select" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOCALES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Dashboard language (viewer UI labels)</Label>
            <Select value={canvas.language || "__auto__"} onValueChange={(v) => onChange({ language: v === "__auto__" ? "" : v })}>
              <SelectTrigger data-testid="canvas-language-select" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => <SelectItem key={l.value || "auto"} value={l.value || "__auto__"}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Translates filter bar, totals, table/pivot labels and empty states (English, العربية, Français, Kiswahili). Arabic switches the published view to right-to-left.</p>
          </div>

          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Chart font (all charts on this dashboard)</Label>
            <Select value={canvas.chart_font || "__theme__"} onValueChange={(v) => onChange({ chart_font: v === "__theme__" ? "" : v })}>
              <SelectTrigger data-testid="canvas-chart-font-select" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__theme__">Theme default (Manrope)</SelectItem>
                {FONT_FAMILIES.filter((f) => f.v).map((f) => (
                  <SelectItem key={f.v} value={f.v}><span style={{ fontFamily: f.v }}>{f.label}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Applies to axis labels, legends and tooltips of every chart — here, in preview and in the published view.</p>
          </div>

          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Pivot table processing</Label>
            <Select value={canvas.pivot_engine || "client"} onValueChange={(v) => onChange({ pivot_engine: v })}>
              <SelectTrigger data-testid="canvas-pivot-engine-select" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client-side — fast, aggregates in the browser</SelectItem>
                <SelectItem value="server">Server-side — exact subtotals from the engine</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Server-side recomputes every subtotal from detail data — exact for averages and calculated measures. Applies to all pivot widgets on this dashboard.</p>
          </div>

          <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-2.5">
            <Label className="text-xs font-medium text-[color:var(--ink-2)]">Filters</Label>
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Where filters appear</Label>
              <Select value={canvas.filter_layout || "bar"} onValueChange={(v) => onChange({ filter_layout: v })}>
                <SelectTrigger data-testid="canvas-filter-layout" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Top bar (default)</SelectItem>
                  <SelectItem value="panel">Side panel with live counts</SelectItem>
                  <SelectItem value="both">Both — panel plus the top bar</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1">The panel is a 280px sidebar on desktop and a slide-over drawer on phones. It reuses these same filters — nothing to set up twice.</p>
            </div>
            {(canvas.filter_layout === "panel" || canvas.filter_layout === "both") && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[color:var(--ink-3)]">Panel width (px)</Label>
                    <Input data-testid="canvas-filter-panel-width" type="number" min={220} max={460}
                      value={(canvas.filter_panel || {}).width ?? 280}
                      onChange={(e) => onChange({ filter_panel: { ...(canvas.filter_panel || {}), width: Number(e.target.value) || 280 } })}
                      className="mt-1 bg-[color:var(--surface)]" />
                  </div>
                  <div>
                    <Label className="text-xs text-[color:var(--ink-3)]">Opens</Label>
                    <Select value={(canvas.filter_panel || {}).collapsed ? "closed" : "open"}
                      onValueChange={(v) => onChange({ filter_panel: { ...(canvas.filter_panel || {}), collapsed: v === "closed" } })}>
                      <SelectTrigger data-testid="canvas-filter-panel-collapsed" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="open">Expanded</SelectItem><SelectItem value="closed">Collapsed</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[color:var(--ink-3)]">Applying filters</Label>
                  <Select value={(canvas.filter_panel || {}).apply_mode === "manual" ? "manual" : "instant"}
                    onValueChange={(v) => onChange({ filter_panel: { ...(canvas.filter_panel || {}), apply_mode: v } })}>
                    <SelectTrigger data-testid="canvas-filter-panel-apply" className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instant">Instantly as people choose</SelectItem>
                      <SelectItem value="manual">Stage everything, one Apply button</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {[["counts", "Show live result counts"], ["chips", "Show active filter chips"], ["show_results", "Show the \u201cShow N results\u201d button"]].map(([k, lbl]) => (
                  <label key={k} className="flex items-center justify-between text-xs text-[color:var(--ink-2)]">
                    {lbl}
                    <input data-testid={`canvas-filter-panel-${k}`} type="checkbox" className="accent-[color:var(--brand-ocean)]"
                      checked={(canvas.filter_panel || {})[k] !== false}
                      onChange={(e) => onChange({ filter_panel: { ...(canvas.filter_panel || {}), [k]: e.target.checked } })} />
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Background color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input data-testid="canvas-bg-color" type="color" value={canvas.bg_color || "#f4f6fb"}
                onChange={(e) => onChange({ bg_color: e.target.value })}
                className="h-9 w-12 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
              <Input value={canvas.bg_color} placeholder="Theme default" onChange={(e) => onChange({ bg_color: e.target.value })} className="bg-[color:var(--surface)]" />
              {canvas.bg_color && (
                <Button size="icon" variant="ghost" onClick={() => onChange({ bg_color: "" })} title="Reset"><X className="w-4 h-4" /></Button>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Wallpaper image</Label>
            <div className="mt-1"><MediaInput testid="canvas-wallpaper-input" value={canvas.wallpaper_url} placeholder="https://…/image.jpg" onChange={(v) => onChange({ wallpaper_url: v })} /></div>
            {canvas.wallpaper_url && (
              <div className="mt-3">
                <Label className="text-xs text-[color:var(--ink-3)]">Wallpaper transparency — {Math.round((canvas.wallpaper_opacity ?? 0.35) * 100)}%</Label>
                <Slider data-testid="canvas-wallpaper-opacity" className="mt-2" min={5} max={100} step={5}
                  value={[Math.round((canvas.wallpaper_opacity ?? 0.35) * 100)]}
                  onValueChange={([v]) => onChange({ wallpaper_opacity: v / 100 })} />
              </div>
            )}
          </div>

          {onApplyTheme && <ThemeSection open={open} canvas={canvas} currentPalette={currentPalette} onApplyTheme={onApplyTheme} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Save current canvas + default palette as a reusable tenant-wide theme; apply themes here too.
function ThemeSection({ open, canvas, currentPalette, onApplyTheme }) {
  const [themes, setThemes] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/dashboard-themes").then(({ data }) => setThemes(data)).catch(() => setThemes([]));
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name the theme"); return; }
    setSaving(true);
    try {
      await api.post("/dashboard-themes", {
        name: name.trim(),
        canvas: { bg_color: canvas.bg_color || "", wallpaper_url: canvas.wallpaper_url || "", wallpaper_opacity: canvas.wallpaper_opacity, locale: canvas.locale || "", chart_font: canvas.chart_font || "" },
        widget_defaults: { palette: currentPalette || "Eazy Core" },
      });
      setName(""); load();
      toast.success("Theme saved — reusable in every dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save theme"); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/dashboard-themes/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="theme-section" className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 space-y-2.5">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" />Dashboard themes (reuse across dashboards)</Label>
      <div className="flex gap-1.5">
        <Input data-testid="theme-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Save current look as…" className="h-8 text-xs bg-[color:var(--surface)]" />
        <Button data-testid="theme-save-btn" size="sm" onClick={save} disabled={saving} className="h-8 bg-[color:var(--brand-ocean)] text-white shrink-0">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <div className="space-y-1.5 max-h-36 overflow-y-auto">
        {themes.map((t) => (
          <div key={t.id} data-testid="theme-row" className="flex items-center gap-2 rounded-lg bg-[color:var(--surface)] px-2.5 py-1.5">
            <span style={{ background: t.canvas?.bg_color || "var(--canvas)" }} className="w-4 h-4 rounded-full border border-[color:var(--glass-border)] shrink-0" />
            <span className="text-xs text-[color:var(--ink-2)] truncate flex-1">{t.name}</span>
            <span className="text-[10px] text-[color:var(--ink-3)]">{t.widget_defaults?.palette}</span>
            <button data-testid="theme-apply-btn" onClick={() => onApplyTheme(t)} title="Apply this theme" className="text-[color:var(--brand-mint)] hover:opacity-80 p-0.5"><Check className="w-3.5 h-3.5" /></button>
            <button data-testid="theme-delete-btn" onClick={() => del(t.id)} title="Delete theme" className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {themes.length === 0 && <p className="text-[11px] text-[color:var(--ink-3)]">No saved themes yet — set colors above and save the look.</p>}
      </div>
    </div>
  );
}
